import type {
  CompositionV3DataQuery,
  PostingsDataQuery,
} from '@revelio/data-access';

import {
  DataProviderQueryStatus,
  DataType,
  type SingleDataSet,
  type TimeseriesData,
  type TimeseriesDataPoint,
  type TimeseriesDataSet,
} from '../types';
import {
  getLatestYoYGrowth,
  inferYoYPeriodOffsetFromDateRefs,
} from '../utils/salary-growth-yoy';
import {
  calculateMarketTightnessMeterValue,
  calculateMeterValue,
} from './z-score/calculate-meter-value';

type CompositionV3 = NonNullable<CompositionV3DataQuery['composition_v3']>;
type CompositionEntity = NonNullable<CompositionV3['entities']>[number];
type PostingResponse = NonNullable<PostingsDataQuery['posting']>;
type PostingEntity = NonNullable<PostingResponse[number]>;
type PostingCategory = PostingEntity['category'];

const buildSalaryValuesAlignedToMonths = (
  entity: CompositionEntity,
  monthsLength: number
): number[] => {
  const values = new Array(monthsLength).fill(0);
  const salaryData = entity.metrics?.salary ?? [];
  for (const point of salaryData) {
    const idx = point.id;
    if (typeof idx === 'number' && idx >= 0 && idx < monthsLength) {
      values[idx] = point.val;
    }
  }
  return values;
};

const getSalaryGrowthForEntity = (
  entity: CompositionEntity,
  months: string[]
): number | null => {
  const periodOffset = inferYoYPeriodOffsetFromDateRefs(months);
  const values = buildSalaryValuesAlignedToMonths(entity, months.length);
  const yoyGrowth = getLatestYoYGrowth(values, periodOffset);
  if (yoyGrowth !== null) {
    return yoyGrowth;
  }

  const salaryData = entity.metrics?.salary ?? [];
  if (salaryData.length < 2) {
    return null;
  }

  const firstValue = salaryData[0]?.val ?? 0;
  const lastValue = salaryData[salaryData.length - 1]?.val ?? 0;

  if (firstValue === 0) {
    return null;
  }

  return (lastValue - firstValue) / firstValue;
};

export function combineTwoQueryStatuses(
  statusA: DataProviderQueryStatus,
  statusB: DataProviderQueryStatus
): DataProviderQueryStatus {
  if (
    statusA === DataProviderQueryStatus.Success &&
    statusB === DataProviderQueryStatus.Success
  ) {
    return DataProviderQueryStatus.Success;
  }
  if (
    statusA === DataProviderQueryStatus.Error ||
    statusB === DataProviderQueryStatus.Error
  ) {
    return DataProviderQueryStatus.Error;
  }
  if (
    statusA === DataProviderQueryStatus.Fetching ||
    statusB === DataProviderQueryStatus.Fetching
  ) {
    return DataProviderQueryStatus.Fetching;
  }
  return DataProviderQueryStatus.Idle;
}

const computeDemandSupplyValue = (
  postings: number,
  headcount: number
): number => (headcount > 0 ? postings / headcount : 0);

const getEntityId = (id: number | null | undefined): number | undefined =>
  typeof id === 'number' ? id : undefined;

const isPostingEntity = (
  entity: PostingResponse[number] | undefined
): entity is PostingEntity => entity !== null && entity !== undefined;

const lastItem = <T>(items: readonly T[] | null | undefined): T | undefined =>
  items?.[items.length - 1];

const getSingleEntityPostingFallback = (
  compositionEntities: CompositionEntity[],
  postingEntities: PostingResponse
): { compositionId: number; postingEntity: PostingEntity } | undefined => {
  if (compositionEntities.length !== 1 || postingEntities.length !== 1) {
    return undefined;
  }
  const compositionId = getEntityId(compositionEntities[0]?.id);
  const postingEntity = postingEntities[0];
  if (compositionId === undefined || !isPostingEntity(postingEntity)) {
    return undefined;
  }
  return { compositionId, postingEntity };
};

const getPostingEntityForCompositionEntity = ({
  compositionEntity,
  compositionEntities,
  postingEntities,
}: {
  compositionEntity: CompositionEntity;
  compositionEntities: CompositionEntity[];
  postingEntities: PostingResponse;
}): PostingEntity | undefined => {
  const id = getEntityId(compositionEntity.id);
  const exactMatch = postingEntities.find(
    (entity): entity is PostingEntity =>
      isPostingEntity(entity) && entity.metadata?.id === id
  );
  if (exactMatch !== undefined) {
    return exactMatch;
  }
  return getSingleEntityPostingFallback(compositionEntities, postingEntities)
    ?.postingEntity;
};

const getLatestPostingMetrics = (
  entity: PostingEntity
): { active: number; timeToFill: number } => {
  const metrics = lastItem(entity.category)?.metrics;
  return {
    active: metrics?.active ?? 0,
    timeToFill: metrics?.time_to_fill ?? 0,
  };
};

const buildPostingValuesByCompositionId = <T>({
  compositionEntities,
  postingEntities,
  getValue,
}: {
  compositionEntities: CompositionEntity[];
  postingEntities: PostingResponse;
  getValue: (entity: PostingEntity) => T;
}): Map<number, T> => {
  const valuesByEntityId = new Map<number, T>();
  for (const entity of postingEntities) {
    if (!isPostingEntity(entity)) {
      continue;
    }
    const id = getEntityId(entity.metadata?.id);
    if (id === undefined) {
      continue;
    }
    valuesByEntityId.set(id, getValue(entity));
  }

  const fallback = getSingleEntityPostingFallback(
    compositionEntities,
    postingEntities
  );
  if (fallback !== undefined && !valuesByEntityId.has(fallback.compositionId)) {
    valuesByEntityId.set(
      fallback.compositionId,
      getValue(fallback.postingEntity)
    );
  }

  return valuesByEntityId;
};

export function computeDemandSupplyDataSet(
  composition: CompositionV3,
  posting: PostingResponse
): SingleDataSet {
  const compositionEntities = composition.entities ?? [];
  const postingEntities = posting ?? [];

  const headcountByEntityId = new Map<
    number,
    { headcount: number; dim: string; id?: number }
  >();
  for (const entity of compositionEntities) {
    const id = entity.id;
    if (id === undefined) {
      continue;
    }
    const headcountData = entity.metrics?.headcount ?? [];
    const headcount =
      headcountData.length > 0
        ? (headcountData[headcountData.length - 1]?.val ?? 0)
        : 0;
    const dim = entity.short_name ?? entity.long_name ?? 'Selected';
    headcountByEntityId.set(id, { headcount, dim, id });
  }

  const activeByEntityId = buildPostingValuesByCompositionId({
    compositionEntities,
    postingEntities,
    getValue: (entity) => getLatestPostingMetrics(entity).active,
  });

  const data: SingleDataSet['data'] = [];
  for (const entity of compositionEntities) {
    const id = entity.id;
    if (id === undefined) {
      continue;
    }
    const headcountEntry = headcountByEntityId.get(id);
    const active = activeByEntityId.get(id);
    if (headcountEntry === undefined || active === undefined) {
      continue;
    }

    const value = computeDemandSupplyValue(active, headcountEntry.headcount);

    data.push({
      dim: headcountEntry.dim,
      value,
      ...(headcountEntry.id !== undefined && { id: String(headcountEntry.id) }),
    });
  }

  return { type: DataType.SINGLE, data };
}

function activeByMonthFromPostingEntity(
  category: PostingCategory
): Map<string, number> {
  const byMonth = new Map<string, { active: number; date: string }>();
  if (!category) {
    return new Map<string, number>();
  }

  for (const timePoint of category) {
    const shortName = timePoint?.metadata?.shortName;
    if (!shortName) {
      continue;
    }
    const monthKey = shortName.slice(0, 7);
    if (monthKey.length !== 7) {
      continue;
    }

    const active = timePoint?.metrics?.active ?? 0;
    const current = byMonth.get(monthKey);
    if (current === undefined || current.date < shortName) {
      byMonth.set(monthKey, { active, date: shortName });
    }
  }

  return new Map(
    Array.from(byMonth.entries(), ([monthKey, data]) => [monthKey, data.active])
  );
}

export function hasDemandSupplyTimeseriesData(
  composition: CompositionV3,
  posting: PostingResponse,
  monthRefs: string[]
): boolean {
  const compositionEntities = composition.entities ?? [];
  const postingEntities = posting ?? [];
  if (compositionEntities.length === 0) {
    return false;
  }

  const months = monthRefs;
  if (!months || months.length <= 1) {
    return false;
  }

  for (const entity of compositionEntities) {
    const id = entity.id;
    if (id === undefined) {
      continue;
    }
    const headcountData = entity.metrics?.headcount ?? [];
    if (headcountData.length <= 1) {
      continue;
    }
    const postingEntity = getPostingEntityForCompositionEntity({
      compositionEntity: entity,
      compositionEntities,
      postingEntities,
    });
    const category = postingEntity?.category;
    if (!category || category.length <= 1) {
      continue;
    }
    return true;
  }
  return false;
}

export function computeDemandSupplyTimeseriesDataSet(
  composition: CompositionV3,
  posting: PostingResponse,
  monthRefs: string[]
): TimeseriesDataSet {
  const compositionEntities = composition.entities ?? [];
  const postingEntities = posting ?? [];
  const months = monthRefs;

  const postingActiveByEntityAndMonth = buildPostingValuesByCompositionId({
    compositionEntities,
    postingEntities,
    getValue: (entity) => activeByMonthFromPostingEntity(entity.category),
  });

  const data: TimeseriesData[] = [];
  for (const entity of compositionEntities) {
    const id = entity.id;
    if (id === undefined) {
      continue;
    }
    const dim = entity.short_name ?? entity.long_name ?? 'Selected';
    const headcountData = entity.metrics?.headcount ?? [];
    const activeByMonth = postingActiveByEntityAndMonth.get(id);
    if (!activeByMonth) {
      continue;
    }

    const timeseries: TimeseriesDataPoint[] = [];
    for (let i = 0; i < headcountData.length; i++) {
      const point = headcountData[i];
      const rawIndex = point?.id ?? i;
      const index =
        typeof rawIndex === 'number' &&
        rawIndex >= 0 &&
        rawIndex < months.length
          ? rawIndex
          : i;
      const date = months[index] ?? '';
      if (!date || !activeByMonth.has(date)) {
        continue;
      }
      const headcount = point?.val ?? 0;
      const active = activeByMonth.get(date) ?? 0;
      timeseries.push({
        date,
        value: computeDemandSupplyValue(active, headcount),
      });
    }
    if (timeseries.length === 0) {
      continue;
    }
    data.push({
      dim,
      timeseries,
      ...(entity.id !== undefined && {
        id: String(entity.id),
      }),
    });
  }
  return { type: DataType.TIMESERIES, data };
}

export function computeDemandSupplyPercentileTimeseriesDataSet(
  composition: CompositionV3,
  posting: PostingResponse,
  monthRefs: string[]
): TimeseriesDataSet {
  const ratioDataSet = computeDemandSupplyTimeseriesDataSet(
    composition,
    posting,
    monthRefs
  );
  const percentileData: TimeseriesData[] = ratioDataSet.data.map((item) => ({
    ...item,
    timeseries: item.timeseries.map((pt) => {
      const percentile =
        calculateMeterValue({
          value: pt.value ?? 0,
          metric: 'demandSupply',
        }) ?? 0;
      return { ...pt, value: percentile };
    }),
  }));
  return { type: DataType.TIMESERIES, data: percentileData };
}

export function computeDemandSupplyDataSetOrTimeseries(
  composition: CompositionV3,
  posting: PostingResponse,
  variant: 'ratio' | 'percentile',
  monthRefs: string[]
): SingleDataSet | TimeseriesDataSet {
  if (hasDemandSupplyTimeseriesData(composition, posting, monthRefs)) {
    const timeSeries =
      variant === 'percentile'
        ? computeDemandSupplyPercentileTimeseriesDataSet(
            composition,
            posting,
            monthRefs
          )
        : computeDemandSupplyTimeseriesDataSet(composition, posting, monthRefs);

    if (timeSeries.data.length > 0) {
      return timeSeries;
    }
  }

  return variant === 'percentile'
    ? computeDemandSupplyPercentileDataSet(composition, posting)
    : computeDemandSupplyDataSet(composition, posting);
}

export function computeDemandSupplyPercentileDataSet(
  composition: CompositionV3,
  posting: PostingResponse
): SingleDataSet {
  const ratioDataSet = computeDemandSupplyDataSet(composition, posting);
  const percentileData = ratioDataSet.data.map((item) => {
    const percentile =
      calculateMeterValue({
        value: item.value ?? 0,
        metric: 'demandSupply',
      }) ?? 0;
    return { ...item, value: percentile };
  });
  return { type: DataType.SINGLE, data: percentileData };
}

export function computeMarketTightnessDataSet(
  composition: CompositionV3,
  posting: PostingResponse,
  monthRefs: string[]
): SingleDataSet {
  const compositionEntities = composition.entities ?? [];
  const postingEntities = posting ?? [];
  const months = monthRefs;

  const compositionByEntityId = new Map<
    number,
    {
      headcount: number;
      dim: string;
      id?: number;
      wageGrowth: number | null;
    }
  >();

  for (const entity of compositionEntities) {
    const id = entity.id;
    if (id === undefined || id === null) {
      continue;
    }

    const metrics = entity.metrics;
    const headcountData = metrics?.headcount ?? [];
    const headcount =
      headcountData.length > 0
        ? (headcountData[headcountData.length - 1]?.val ?? 0)
        : 0;

    const wageGrowth = getSalaryGrowthForEntity(entity, months);

    const dim = entity.short_name ?? entity.long_name ?? 'Selected';

    compositionByEntityId.set(id, {
      headcount,
      dim,
      id,
      wageGrowth,
    });
  }

  const postingsByEntityId = buildPostingValuesByCompositionId({
    compositionEntities,
    postingEntities,
    getValue: getLatestPostingMetrics,
  });

  const data: SingleDataSet['data'] = [];

  for (const entity of compositionEntities) {
    const id = entity.id;
    if (id === undefined || id === null) {
      continue;
    }

    const compositionEntry = compositionByEntityId.get(id);
    const postingsEntry = postingsByEntityId.get(id);
    if (!compositionEntry || !postingsEntry) {
      continue;
    }

    const { headcount, wageGrowth } = compositionEntry;
    const { active, timeToFill } = postingsEntry;

    if (wageGrowth === null) {
      continue;
    }

    const demandSupply = computeDemandSupplyValue(active, headcount);

    const value = calculateMarketTightnessMeterValue({
      wageGrowth,
      demandSupply,
      timeToFill,
    });

    data.push({
      dim: compositionEntry.dim,
      value,
      ...(compositionEntry.id !== undefined && {
        id: String(compositionEntry.id),
      }),
    });
  }

  return { type: DataType.SINGLE, data };
}
